import React, {useEffect} from "react";
import { Box, Grid, Typography, makeStyles, Button, useTheme, useMediaQuery } from '@material-ui/core';
import writtenNumber from "written-number";

import { CustomSwitch } from "../components/Switch/Switch";
import RangeSlider from "../components/RangeSlider";
import { whiteGrey, accentDefault } from "../constants/colors";
import { menuSettings } from "../constants/settings";
import NexIcon from '../assets/img/next.svg';
import { TravelCareType } from '../api/enums';
import { getCheckPaymentPs } from '../utils/productUtils';

const useStyles = makeStyles(({ breakpoints: { down } }) => ({
  flightSummary: {
    width: "100%",
    padding: "30px 40px",
    [down("sm")]: {
      background: whiteGrey,
      borderRadius: "10px",
      marginTop: "30px",
    },
  },
  summaryBox: {
    padding: "20px 0",
    borderBottom: "1px solid rgba(0, 0, 0, 0.3)",
  },
  customFontSize: {
    fontSize: "14px",
  },
  addTicketProtection: {
    transition: "all 0.3s ease",
    background: "rgb(74 148 236 / 10%)",
    borderRadius: "10px",
    color: accentDefault,
    fontSize: "10px",
    textTransform: "uppercase",
    padding: "10px 15px",
    fontFamily: "sbcBold",
    cursor: "pointer",
  },
  yourIp: {
    fontSize: "12px",
  }
}));

const AdditionalExpenseSummary = (
  {
    confirmTerms,
    splitPaymentValue,
    cards,
    register,
    setValue,
    apiData,
    cardsAmmount,
    handleNext,
    acceptTipsValue,
    tipsValue,
    manualSplitValue,
    isForceManualSplitValue,
    previewMode
  }
) => {
  const classes = useStyles();

  useEffect(() => {
    setValue("totalAmount", totalPriceCalc())
  }, [apiData, tipsValue, acceptTipsValue, manualSplitValue]);

  useEffect( () => {
    const isForceManualSplit = acceptTipsValue ? cards.length > 2 : cards.length > 1
    setValue('manualSplit', isForceManualSplit)
    setValue('isForceManualSplit', isForceManualSplit)
  }, [acceptTipsValue, cards.length])

  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));


  const inhouseCharge = () => {
    const ckPs = getCheckPaymentPs( apiData ) + 1
    const {tbc, airline} = apiData.taxes

    return manualSplitValue ? tbc + airline * ckPs : tbc
  }

  const airlineCharge = () => {
    const {tbc, airline} = apiData.taxes

    return manualSplitValue ? 0 : airline
  }

  const priceOfferCalc = () => {
    return inhouseCharge() + airlineCharge()
  }


  const calcAdditionalService = () => {
    let additionalPrice = 0;
    if (acceptTipsValue) {
      additionalPrice += Number(tipsValue);
    }

    return additionalPrice?.toFixed(2);
  };

  const totalPriceCalc = () => {
    let price = priceOfferCalc();

    if (acceptTipsValue) {
      price += Number(tipsValue);
    }

    return price.toFixed(2);
  };

  const numberToWrittenString = () => {
    const finalPrice = totalPriceCalc()
    const splittedPrice = finalPrice.split(".");
    return (
      <>{`(${writtenNumber(splittedPrice[0])} US dollars and ${writtenNumber(
        splittedPrice[1]
      )} cents)`}</>
    );
  };

  const yourIPString = () => {
    if(!apiData?.user_ip)
      return ''

    return `Your IP: ${apiData.user_ip}`;
  };

  return (
    <Box className={classes.flightSummary}>
      <Grid container className={classes.summaryBox}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h4">
                {apiData.products[0].type}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4">{`$ ${priceOfferCalc().toFixed( 2 )}`}</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid container item xs={12} justifyContent="flex-end">
          <Grid item xs={12} sm={8}>
            {!manualSplitValue && !!apiData?.taxes.airline && (
              <Box display="flex" justifyContent="space-between" mb={1.5}>
                <Box>
                  <Typography variant="caption">
                    To be processed by airline
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption">
                    <b>{`$ ${apiData?.taxes?.airline.toFixed(2)}`}</b>
                  </Typography>
                </Box>
              </Box>
            )}
            { !!apiData?.taxes.tbc && (
              <Box display="flex" justifyContent="space-between" mb={1.5}>
                <Box>
                  <Typography variant="caption">
                    To be processed by {process.env?.REACT_APP_PROJECT?.toUpperCase()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption">
                    <b>{`$ ${
                      !manualSplitValue ?
                        apiData?.taxes?.tbc.toFixed(2) :
                        priceOfferCalc().toFixed(2)
                    }`}</b>
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid container className={classes.summaryBox}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h4">Additional Service</Typography>
            </Box>
            <Box>
              <Typography variant="h4">
                {`$ ${calcAdditionalService()}`}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid container item xs={12} justify="flex-end">
          <Grid item xs={12} sm={8}>
            <Box display="flex" justifyContent="space-between" mb={1.5}>
              <Box>
                <Typography variant="caption">
                  Tips for excellent service
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">
                  <b>{`$ ${
                    (acceptTipsValue && Number(tipsValue).toFixed(2)) || "0.00"
                  }`}</b>
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" mt={2} mb={2}>
            <Box display="flex" alignItems="center">
              <Typography variant="h2">Total:</Typography>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Typography variant="h1" className="green">
                {`$ ${totalPriceCalc()}`}
              </Typography>
              <Typography
                variant="caption"
                align="right"
                style={{ maxWidth: "300px" }}
              >
                {numberToWrittenString()}
              </Typography>
            </Box>
          </Box>
          <Box mt={2} mb={3}>
            <CustomSwitch
              name="confirmTerms"
              value={confirmTerms}
              label={
                <Box>
                  <Box mb={1}>
                    <Typography variant="h4">
                      By activating this button:
                    </Typography>
                  </Box>
                  <Typography className={classes.customFontSize}>
                    1. I agree that my credit card will be charged the above
                    total amount (your card may be billed in multiple charges
                    totaling the final price). 2. I
                    accept the{" "}
                    <a
                      href={menuSettings.link.terms}
                      className="blue"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms and Conditions
                    </a>{" "}
                    and{" "}
                    <a
                      href={menuSettings.link.privacy}
                      className="blue"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </Typography>
                </Box>
              }
            />
          </Box>
          {splitPaymentValue && (
            <Box mt={1} mb={2}>
              <RangeSlider
                max={totalPriceCalc()}
                cards={cards}
                register={register}
                setValue={setValue}
                initialAmmounts={cardsAmmount}

                manualSplitValue={manualSplitValue}
                selectedFlight={apiData}
                tipsValue={tipsValue}
                acceptTipsValue={acceptTipsValue}
                travelCareValue={TravelCareType.Decline}
                isForceManualSplitValue={isForceManualSplitValue}
              />
            </Box>
          )}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="caption"
              className={classes.yourIp}
            >
              {yourIPString()}
            </Typography>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={!confirmTerms && !previewMode}
                mt={2}
              >
                <Typography>{sm ? "Confirm" : "Confirm payment"}</Typography>
                <Box ml={0.5} display="flex">
                  <img src={NexIcon} alt="next-icon" />
                </Box>
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdditionalExpenseSummary;
